import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, styled, useTheme } from '@mui/material';
import {
  IconChevronDown,
  IconChevronUp,
  IconInfoCircle,
  IconPencil,
} from '@tabler/icons-react';

import {
  Button,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from '@zendesk/forethought-elements';
import { getRatingOptions } from './conversationReview';
import flagHeartIcon from 'src/assets/images/icon-flag-heart.svg';
import { useGetConversationReviewQuery } from 'src/services/insights';
import { ConversationReviewRating } from 'src/services/insights/types';

// Lines to show before clamping the comment in the submitted-state card.
// Matches the Figma collapsed state (≈5 lines before truncation + chevron).
const COMMENT_CLAMP_LINES = 5;

interface VerdictCardProps {
  conversationId: string;
  isFlagged?: boolean;
  /** Granted users with view-only access on the conversation's channel see the review but can't write one. */
  isReadOnly?: boolean;
  onOpenFeedbackPanel: (
    initialRating?: ConversationReviewRating,
    initialComment?: string | null,
  ) => void;
}

export const VerdictCard = ({
  conversationId,
  isFlagged,
  isReadOnly = false,
  onOpenFeedbackPanel,
}: VerdictCardProps) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();
  const ratingOptions = getRatingOptions(t);
  const { data: review, isLoading } = useGetConversationReviewQuery(
    { conversation_id: conversationId },
    { skip: !conversationId },
  );

  // Auto-saved positive ratings land in the backend with is_submitted=false
  // — the user picked "good enough" but never hit Submit. Treat those as
  // still-pending so we keep showing the initial entry-point tiles rather
  // than the read-out card, which would otherwise make the rating look
  // committed and surprise the user when they reopen the conversation.
  const ratedOption = review?.is_submitted
    ? ratingOptions.find(o => o.value === review.rating)
    : undefined;

  // Comment can be long enough to need a "show more / show less" affordance.
  // We measure scrollHeight vs clientHeight while clamped to decide whether
  // to show the chevron. Skip the measurement while expanded — at that point
  // the clamp is removed so the two heights would match and we'd lose the
  // chevron prematurely; instead we trust the previously-captured value.
  const [isCommentExpanded, setIsCommentExpanded] = useState(false);
  const [hasCommentOverflow, setHasCommentOverflow] = useState(false);
  const commentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    // Empty comment renders a placeholder instead of the measured Box —
    // there's nothing to clamp, so the chevron stays hidden.
    if (!review?.comment) {
      setHasCommentOverflow(false);
      return;
    }
    const el = commentRef.current;
    if (!el || isCommentExpanded) {
      return;
    }
    // +1 to absorb sub-pixel rounding so we don't show the chevron for
    // comments that fit exactly within the clamp height.
    setHasCommentOverflow(el.scrollHeight > el.clientHeight + 1);
  }, [review?.comment, isCommentExpanded]);

  return (
    <CardContainer>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: '8px',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', gap: '8px' }}>
          <Typography variant='font14Bold'>{t('verdictCard.title')}</Typography>
          <Tooltip tooltipContent={t('verdictCard.tooltip')}>
            <IconInfoCircle color={palette.colors.grey[500]} size={14} />
          </Tooltip>
          {isFlagged && (
            <FlaggedBadge>
              <img
                alt=''
                height={14}
                src={flagHeartIcon}
                style={{ marginTop: '1px' }}
                width={14}
              />
              {t('verdictCard.flaggedBadge')}
            </FlaggedBadge>
          )}
        </Box>
        {ratedOption && !isReadOnly && (
          <IconButton
            aria-label={t('verdictCard.editVerdictAriaLabel')}
            onClick={() => onOpenFeedbackPanel(review?.rating, review?.comment)}
            variant='ghost'
          >
            <IconPencil size={16} />
          </IconButton>
        )}
      </Box>

      <Typography color={palette.colors.grey[800]} variant='font12'>
        {t('verdictCard.description')}
      </Typography>

      {isLoading ? (
        <Skeleton height='80px' width='100%' />
      ) : ratedOption ? (
        <>
          <Box
            sx={{
              alignItems: 'stretch',
              display: 'flex',
              gap: '16px',
              // Anchors the visual height so both columns and the divider
              // read as a balanced block even when the comment is short
              // or empty.
              minHeight: '105px',
            }}
          >
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                flexShrink: 0,
                gap: '8px',
                justifyContent: 'center',
                width: '102px',
              }}
            >
              <Box sx={{ fontSize: '40px', lineHeight: 1 }}>
                {ratedOption.emoji}
              </Box>
              <Typography variant='font12Medium'>
                {ratedOption.label}
              </Typography>
            </Box>
            <Box
              sx={{
                alignSelf: 'stretch',
                borderLeft: `1px solid ${palette.colors.slate[200]}`,
              }}
            />
            <Box
              sx={{
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                gap: '4px',
                // Vertically centers the Comment header + body within the
                // 105px row so it lines up with the emoji column.
                justifyContent: 'center',
                minWidth: 0,
              }}
            >
              <Typography variant='font12Medium'>
                {t('feedbackPanel.commentAriaLabel')}
              </Typography>
              {review?.comment ? (
                // Wrap Typography in a Box that does the line-clamp so the
                // design-system font (family, weight, letter-spacing) still
                // applies via the variant — Typography just renders the text.
                <Box
                  ref={commentRef}
                  sx={{
                    wordBreak: 'break-word',
                    ...(isCommentExpanded
                      ? {}
                      : {
                          display: '-webkit-box',
                          overflow: 'hidden',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: COMMENT_CLAMP_LINES,
                        }),
                  }}
                >
                  <Typography
                    color={palette.colors.grey[600]}
                    variant='font12Medium'
                  >
                    {review.comment}
                  </Typography>
                </Box>
              ) : (
                <i>
                  <Typography
                    color={palette.colors.grey[600]}
                    variant='font12Medium'
                  >
                    {t('verdictCard.emptyCommentPlaceholder')}
                  </Typography>
                </i>
              )}
            </Box>
          </Box>
          {review?.comment && hasCommentOverflow && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: '-12px',
                mt: '-4px',
              }}
            >
              <IconButton
                aria-label={
                  isCommentExpanded
                    ? t('verdictCard.collapseCommentAriaLabel')
                    : t('verdictCard.expandCommentAriaLabel')
                }
                onClick={() => setIsCommentExpanded(prev => !prev)}
                variant='ghost'
              >
                {isCommentExpanded ? (
                  <IconChevronUp size={16} />
                ) : (
                  <IconChevronDown size={16} />
                )}
              </IconButton>
            </Box>
          )}
        </>
      ) : isReadOnly ? (
        <Typography color={palette.colors.grey[600]} variant='font12'>
          {t('verdictCard.noReviewYet')}
        </Typography>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              gap: '24px',
              justifyContent: 'center',
              py: '8px',
            }}
          >
            {ratingOptions.map(option => (
              <RatingOption
                aria-label={option.label}
                key={option.value}
                onClick={() =>
                  onOpenFeedbackPanel(option.value, review?.comment)
                }
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpenFeedbackPanel(option.value, review?.comment);
                  }
                }}
                role='button'
                tabIndex={0}
              >
                <Box sx={{ fontSize: '32px', lineHeight: 1 }}>
                  {option.emoji}
                </Box>
                <Typography color={palette.colors.grey[600]} variant='font14'>
                  {option.label}
                </Typography>
              </RatingOption>
            ))}
          </Box>
          <Button
            fullWidth
            onClick={() => onOpenFeedbackPanel(review?.rating, review?.comment)}
            style={{ color: palette.colors.teal[700], fontWeight: 600 }}
            variant='secondary'
          >
            {t('verdictCard.giveFeedbackButton')}
          </Button>
        </>
      )}
    </CardContainer>
  );
};

const CardContainer = styled(Box)`
  border: 1px solid ${({ theme }) => theme.palette.colors.slate[200]};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
`;

const FlaggedBadge = styled(Box)`
  align-items: center;
  background-color: ${({ theme }) => theme.palette.colors.yellow[400]};
  border-radius: 6px;
  color: ${({ theme }) => theme.palette.colors.black};
  display: inline-flex;
  font-size: 12px;
  font-weight: 600;
  gap: 4px;
  line-height: 1.5;
  padding: 4px 8px;
`;

const RatingOption = styled(Box)`
  align-items: center;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 4px 8px;
  &:hover {
    background-color: ${({ theme }) => theme.palette.colors.slate[100]};
  }
`;
